import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import axiosInstanceAuth from "../../apiInstances/axiosInstanceAuth";
import { useCollections } from "../../Hooks/useCollections/useCollections";

export default function CollectionCreateModal({
  open,
  onClose,
  editingCollection,
  onSaved,
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { refetch } = useCollections();

  useEffect(() => {
    if (open) {
      setName(editingCollection?.name ?? "");
      setDescription(editingCollection?.description ?? "");
      setError("");
    }
  }, [open, editingCollection]);

  if (!open) return null;

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setLoading(true);
    setError("");
    try {
      let result = null;
      if (editingCollection) {
        const res = await axiosInstanceAuth.put(
          `/collection/${editingCollection.id}`,
          {
            name: trimmed,
            description: description.trim() || "",
          }
        );
        result = res?.data?.data ?? res?.data ?? {
          ...editingCollection,
          name: trimmed,
          description: description.trim() || "",
        };
      } else {
        const res = await axiosInstanceAuth.post("/collection", {
          name: trimmed,
          description: description.trim() || "",
        });
        result = res?.data?.data ?? res?.data;
      }
      onSaved && onSaved(result);
      refetch();
      onClose();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
        e?.message ||
        (editingCollection
          ? "Failed to update collection"
          : "Failed to create collection")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-semibold text-[#000929]">
            {editingCollection ? "Update Collection" : "Create Collection"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Collection name"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-[#000929] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#097FE6] focus:border-transparent mb-3"
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
        />
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-[#000929] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#097FE6] focus:border-transparent mb-5"
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
        />
        {error && (
          <p className="text-red-600 text-sm mb-3">{error}</p>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={!name.trim() || loading}
          className="w-full py-3 rounded-full bg-[#097FE6] text-white font-medium hover:bg-[#0664b7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading
            ? editingCollection
              ? "Updating..."
              : "Saving..."
            : editingCollection
              ? "Update"
              : "Save"}
        </button>
      </div>
    </div>
  );
}

